import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Checkbox
} from '@mui/material';
import Grid from '@mui/material/Grid';
import type { SelectChangeEvent } from '@mui/material/Select';

import {
  Business,
  People,
  PendingActions,
  Add,
  Edit,
  Check,
  Close,
  Visibility,
  Search,
  Refresh,
  Download,
  Assignment,
  Delete
} from '@mui/icons-material';
import ValidadoLogo from '../components/ValidadoLogo';

interface Company {
  id: string;
  name: string;
  industry: string;
  address: string;
  phone: string;
  email: string;
  website?: string;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  employeeCount: number;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'manager' | 'employee';
  companyId: string;
  companyName: string;
  status: 'approved' | 'pending' | 'rejected';
  createdAt: string;
  lastLogin?: string;
}

interface PendingApproval {
  id: string;
  type: 'user' | 'company';
  name: string;
  email: string;
  companyName?: string;
  role?: string;
  requestDate: string;
  message?: string;
}

interface BackgroundCheck {
  id: string;
  nationalId?: string; // Direct national ID for nationalId-only checks
  client?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    ssn?: string; // SSN from client
    nationalId?: string; // National ID from client (legacy)
  };
  company: {
    id: string;
    name: string;
  };
  requestedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  type: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  overallResult?: 'clear' | 'consider' | 'not_clear' | 'pending';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  requestDate: string;
  estimatedCompletionDate?: string;
  completionDate?: string;
  results?: any;
  notes?: string;
}

type CheckAction =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'completed_clear'
  | 'completed_not_clear'
  | 'failed'
  | 'cancelled';

type DownloadMode = 'status_sample' | 'detailed_checks';

const getCheckNationalId = (check: BackgroundCheck): string =>
  check.client?.nationalId || check.nationalId || '';

const getCheckDisplayName = (check: BackgroundCheck): string => {
  const clientName = `${check.client?.firstName || ''} ${check.client?.lastName || ''}`.trim();
  if (clientName) {
    return clientName;
  }

  try {
    const parsedNotes = check.notes ? JSON.parse(check.notes) : null;
    return `${parsedNotes?.firstName || ''} ${parsedNotes?.lastName || ''}`.trim();
  } catch (error) {
    return '';
  }
};

const escapeCsvField = (value: string | number | null | undefined): string =>
  `"${String(value ?? '').replace(/"/g, '""')}"`;

const formatNationalIdForExcel = (value: string | undefined): string => {
  const nationalId = String(value ?? '').trim();
  return nationalId ? `="${nationalId}"` : '';
};

const normalizeCompletionOutcome = (value: string): 'clear' | 'not_clear' | null => {
  const normalized = value.trim().toLowerCase().replace(/^['"]+|['"]+$/g, '');
  const compact = normalized.replace(/[\s_-]+/g, '');

  if (['aprobado', 'approved', 'passed', 'pass', 'clear'].includes(compact)) {
    return 'clear';
  }

  if (['fallido', 'failed', 'fail', 'notclear'].includes(compact)) {
    return 'not_clear';
  }

  return null;
};

const parseCheckAction = (action: string): {
  status: BackgroundCheck['status'];
  overallResult?: 'clear' | 'not_clear';
  label: string;
} | null => {
  switch (action as CheckAction) {
    case 'pending':
      return { status: 'pending', label: 'Pending' };
    case 'in_progress':
      return { status: 'in_progress', label: 'In Progress' };
    case 'completed':
      return { status: 'completed', label: 'Completed' };
    case 'completed_clear':
      return { status: 'completed', overallResult: 'clear', label: 'Completed + Clear' };
    case 'completed_not_clear':
      return { status: 'completed', overallResult: 'not_clear', label: 'Completed + Not Clear' };
    case 'failed':
      return { status: 'failed', label: 'Failed' };
    case 'cancelled':
      return { status: 'cancelled', label: 'Cancelled' };
    default:
      return null;
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ValidadoAdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  
  // State for companies
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [viewingCompany, setViewingCompany] = useState<Company | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // State for users
  const [users, setUsers] = useState<User[]>([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: '', // Leave empty for edit
      role: user.role,
      companyId: user.companyId || '',
      department: '',
      position: ''
    });
    setUserDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          firstName: userForm.firstName,
          lastName: userForm.lastName,
          email: userForm.email,
          role: userForm.role,
          companyId: userForm.companyId,
          ...(userForm.password ? { password: userForm.password } : {})
        }),
      });

      if (response.ok) {
        setAlert({ type: 'success', message: 'User updated successfully!' });
        setUserDialogOpen(false);
        setEditingUser(null);
        setUserForm({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          role: 'employee',
          companyId: '',
          department: '',
          position: ''
        });
        loadUsers();
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.message || 'Failed to update user' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Network error occurred' });
    } finally {
      setLoading(false);
    }
  };
  
  // State for pending approvals
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  
  // State for background checks
  const [checks, setChecks] = useState<BackgroundCheck[]>([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState<BackgroundCheck | null>(null);
  const [checkViewDialogOpen, setCheckViewDialogOpen] = useState(false);
  const [checksStatusFilter, setChecksStatusFilter] = useState('all');
  const [requestDateStart, setRequestDateStart] = useState('');
  const [requestDateEnd, setRequestDateEnd] = useState('');
  const [lastUpdatedStart, setLastUpdatedStart] = useState('');
  const [lastUpdatedEnd, setLastUpdatedEnd] = useState('');
  const [checksSearch, setChecksSearch] = useState('');
  const [downloadMode, setDownloadMode] = useState<DownloadMode>('status_sample');
  const [selectedChecks, setSelectedChecks] = useState<string[]>([]);
  const [checksStats, setChecksStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    failed: 0
  });
  
  // State for alerts and loading
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Search and filter states
  const [companySearch, setCompanySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Company form state
  const [companyForm, setCompanyForm] = useState({
    name: '',
    industry: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    size: '1-10',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    street: '',
    city: '',
    state: '',
    zipCode: ''
  });

  // User form state
  const [userForm, setUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'employee' as 'admin' | 'manager' | 'employee',
    companyId: '',
    department: '',
    position: ''
  });

  const handleLogin = async () => {
    setLoading(true);
    setLoginError('');
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        const token = data.data?.token || data.token;
        
        // Store the JWT token
        localStorage.setItem('adminToken', token);
        
        // Verify user has admin privileges
        const meResponse = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (meResponse.ok) {
          const userData = await meResponse.json();
          const user = userData.data?.user || userData.data || userData.user;
          
          // Check if user is admin or super admin
          if (user && (user.role === 'admin' || user.role === 'super_admin' || email === 'tapia@over.do')) {
            setIsAuthenticated(true);
            setLoginError('');
          } else {
            setLoginError('Access denied. Admin privileges required.');
            localStorage.removeItem('adminToken');
          }
        } else {
          setLoginError('Failed to verify user permissions');
          localStorage.removeItem('adminToken');
        }
      } else {
        const errorData = await response.json();
        setLoginError(errorData.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate actual employee count from users data
  const getActualEmployeeCount = (companyName: string): number => {
    return users.filter(user => 
      user.companyName === companyName && 
      user.status === 'approved'
    ).length;
  };

  const handleApproveUser = async (userId: string, action: 'approve' | 'reject') => {
    setLoading(true);
    try {
      // In a real implementation, this would call the company management API
      // For now, we'll simulate the approval process
      const response = await fetch(`/api/company/authorize-user/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || 'admin-token'}`
        },
        body: JSON.stringify({
          action,
          notes: `${action === 'approve' ? 'Approved' : 'Rejected'} by Validado admin`
        }),
      });

      if (response.ok) {
        setAlert({ 
          type: 'success', 
          message: `User ${action === 'approve' ? 'approved' : 'rejected'} successfully!` 
        });
        loadPendingApprovals();
        loadUsers();
      } else {
        setAlert({ type: 'error', message: `Failed to ${action} user` });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Network error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/companies', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies((data.data || []).map((company: any) => ({
          id: company.id,
          name: company.name,
          industry: company.industry || 'Not specified',
          address: company.address || 'Not specified',
          phone: company.phone || 'Not specified',
          email: company.email || 'Not specified',
          website: company.website || '',
          status: 'active',
          createdAt: company.created_at || company.createdAt || new Date().toISOString(),
          employeeCount: company.employeeCount || 0
        })));
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('No admin token found');
        setUsers([]);
        return;
      }

      // Load all users from all companies (admin view)
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle different response formats
        const usersData = data.data || data || [];
        setUsers(Array.isArray(usersData) ? usersData : []);
      } else {
        console.error('Failed to load users:', response.status, response.statusText);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  }, []);

  const loadPendingApprovals = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      // Load pending users from all companies
      const companiesResponse = await fetch('/api/companies', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (companiesResponse.ok) {
        await companiesResponse.json();
        const allPendingApprovals: PendingApproval[] = [];
        
        // For each company, we would need to get pending users
        // For now, we'll create mock pending approvals based on recent join requests
        // In a real implementation, you'd have a dedicated endpoint for this
        
        setPendingApprovals(allPendingApprovals);
      }
    } catch (error) {
      console.error('Error loading pending approvals:', error);
      setPendingApprovals([]);
    }
  }, []);

  const loadChecks = useCallback(async () => {
    setChecksLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.error('No admin token found');
        setChecks([]);
        setChecksStats({ total: 0, pending: 0, inProgress: 0, completed: 0, failed: 0 });
        setChecksLoading(false);
        return;
      }
      const url = checksStatusFilter === 'all' 
        ? '/api/checks' 
        : `/api/checks?status=${encodeURIComponent(checksStatusFilter)}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle different response formats
        const checksData = Array.isArray(data) ? data : (data.data || []);
        setChecks(checksData);
        // Prefer stats from API if available, fallback to local calculation
        const apiStats = (data.stats || data.meta?.stats);
        if (apiStats) {
          setChecksStats({
            total: apiStats.total ?? checksData.length,
            pending: apiStats.pending ?? 0,
            inProgress: (apiStats.in_progress ?? apiStats.inProgress ?? 0),
            completed: apiStats.completed ?? 0,
            failed: apiStats.failed ?? 0
          });
        } else {
          const stats = {
            total: checksData.length,
            pending: checksData.filter((c: BackgroundCheck) => c.status === 'pending').length,
            inProgress: checksData.filter((c: BackgroundCheck) => c.status === 'in_progress').length,
            completed: checksData.filter((c: BackgroundCheck) => c.status === 'completed').length,
            failed: checksData.filter((c: BackgroundCheck) => c.status === 'failed').length
          };
          setChecksStats(stats);
        }
      } else {
        console.error('Failed to load checks:', response.status, response.statusText);
        setChecks([]);
        setChecksStats({ total: 0, pending: 0, inProgress: 0, completed: 0, failed: 0 });
      }
    } catch (error) {
      console.error('Error loading checks:', error);
      setChecks([]);
      setChecksStats({ total: 0, pending: 0, inProgress: 0, completed: 0, failed: 0 });
    } finally {
      setChecksLoading(false);
    }
  }, [checksStatusFilter]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCompanies(),
        loadUsers(),
        loadPendingApprovals(),
        loadChecks()
      ]);
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  }, [loadChecks, loadCompanies, loadPendingApprovals, loadUsers]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, loadData]);

  const handleExportCSV = async () => {
    await handleExportChecks();
  };

  const handleViewCheck = (check: BackgroundCheck) => {
    setSelectedCheck(check);
    setCheckViewDialogOpen(true);
  };

  const handleSelectCheck = (checkId: string) => {
    setSelectedChecks(prev => 
      prev.includes(checkId) 
        ? prev.filter(id => id !== checkId)
        : [...prev, checkId]
    );
  };

  const handleSelectAllChecks = () => {
    if (selectedChecks.length === filteredChecks.length) {
      setSelectedChecks([]);
    } else {
      setSelectedChecks(filteredChecks.map(check => check.id));
    }
  };

  const handleBulkStatusChange = async (action: string) => {
    if (selectedChecks.length === 0) return;

    const parsedAction = parseCheckAction(action);
    if (!parsedAction) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/checks/bulk-status', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          checkIds: selectedChecks,
          status: parsedAction.status,
          ...(parsedAction.overallResult ? { overallResult: parsedAction.overallResult } : {})
        })
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to update checks');
      }

      const updatedCount = data?.data?.updated ?? selectedChecks.length;
      const skippedCount = data?.data?.skipped ?? 0;
      const message = skippedCount > 0
        ? `Updated ${updatedCount} checks to ${parsedAction.label}. ${skippedCount} were skipped.`
        : `Updated ${updatedCount} checks to ${parsedAction.label}`;

      setAlert({ type: 'success', message });
      setSelectedChecks([]);
      loadChecks();
    } catch (error) {
      console.error('Error updating checks:', error);
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update checks'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedChecks.length === 0) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete ${selectedChecks.length} background check${selectedChecks.length !== 1 ? 's' : ''}? This action cannot be undone.`);
    if (!confirmed) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const promises = selectedChecks.map(checkId =>
        fetch(`/api/checks/${checkId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      );
      
      await Promise.all(promises);
      setAlert({ type: 'success', message: `Deleted ${selectedChecks.length} background check${selectedChecks.length !== 1 ? 's' : ''}` });
      setSelectedChecks([]);
      loadChecks();
    } catch (error) {
      console.error('Error deleting checks:', error);
      setAlert({ type: 'error', message: 'Failed to delete checks' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (checkId: string, action: string) => {
    const parsedAction = parseCheckAction(action);
    if (!parsedAction) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/checks/${checkId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: parsedAction.status,
          ...(parsedAction.overallResult ? { overallResult: parsedAction.overallResult } : {})
        })
      });

      if (response.ok) {
        setAlert({ type: 'success', message: `Check updated to ${parsedAction.label}` });
        loadChecks(); // Reload checks to reflect the change
      } else {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating check status:', error);
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update check status'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportChecks = async () => {
    try {
      const checksToExport = selectedChecks.length > 0 
        ? filteredChecks.filter(check => selectedChecks.includes(check.id))
        : filteredChecks;

      if (checksToExport.length === 0) {
        setAlert({ type: 'info', message: 'No checks to export' });
        return;
      }

      // Create CSV content
      const isStatusSample = downloadMode === 'status_sample';
      const headers = isStatusSample
        ? ['National ID', 'Name', 'Status']
        : ['National ID', 'Name', 'Company', 'Type', 'Status', 'Result', 'Upload Status', 'Priority', 'Request Date', 'Completion Date'];
      const csvContent = '\uFEFF' + [
        headers.join(','),
        ...checksToExport.map(check => {
          const currentResult = check.overallResult && check.overallResult !== 'pending'
            ? check.overallResult.replace('_', ' ')
            : '';

          const row = isStatusSample
            ? [
                escapeCsvField(formatNationalIdForExcel(getCheckNationalId(check))),
                escapeCsvField(getCheckDisplayName(check)),
                escapeCsvField(currentResult)
              ]
            : [
                escapeCsvField(formatNationalIdForExcel(getCheckNationalId(check))),
                escapeCsvField(getCheckDisplayName(check)),
                escapeCsvField(check.company.name),
                escapeCsvField(check.type),
                escapeCsvField(check.status),
                escapeCsvField(currentResult),
                escapeCsvField(currentResult),
                escapeCsvField(check.priority),
                escapeCsvField(new Date(check.requestDate).toLocaleDateString()),
                escapeCsvField(check.completionDate ? new Date(check.completionDate).toLocaleDateString() : '')
              ];

          return row.join(',');
        })
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `${downloadMode === 'status_sample' ? 'status_update_sample' : 'background_checks'}_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setAlert({
        type: 'success',
        message: downloadMode === 'status_sample'
          ? `Downloaded sample CSV for ${checksToExport.length} background checks`
          : `Exported ${checksToExport.length} background checks`
      });
    } catch (error) {
      console.error('Error exporting checks:', error);
      setAlert({ type: 'error', message: 'Failed to export checks' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const filteredChecks = checks.filter(check => {
    const matchesSearch = checksSearch === '' || 
      check.client?.ssn?.toLowerCase().includes(checksSearch.toLowerCase()) ||
      check.nationalId?.toLowerCase().includes(checksSearch.toLowerCase()) ||
      check.company.name.toLowerCase().includes(checksSearch.toLowerCase());
    
    const matchesStatus = checksStatusFilter === 'all' || check.status === checksStatusFilter;
    
    let matchesReqDate = true;
    if (requestDateStart) {
      matchesReqDate = matchesReqDate && new Date(check.requestDate) >= new Date(requestDateStart);
    }
    if (requestDateEnd) {
      const end = new Date(requestDateEnd);
      end.setHours(23, 59, 59, 999);
      matchesReqDate = matchesReqDate && new Date(check.requestDate) <= end;
    }

    let matchesUpdatedDate = true;
    if (lastUpdatedStart || lastUpdatedEnd) {
      const dateToCompareStr = check.completionDate || (check as any).updatedAt || (check as any).updated_at;
      if (!dateToCompareStr) {
        matchesUpdatedDate = false;
      } else {
        const dateToCompare = new Date(dateToCompareStr);
        if (lastUpdatedStart) {
          matchesUpdatedDate = matchesUpdatedDate && dateToCompare >= new Date(lastUpdatedStart);
        }
        if (lastUpdatedEnd) {
          const end = new Date(lastUpdatedEnd);
          end.setHours(23, 59, 59, 999);
          matchesUpdatedDate = matchesUpdatedDate && dateToCompare <= end;
        }
      }
    }

    return matchesSearch && matchesStatus && matchesReqDate && matchesUpdatedDate;
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setChecksLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const updates: { nationalId: string, status: 'clear' | 'not_clear' }[] = [];

        // Parse CSV assuming format: National ID, Name, Status
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          // Split by tab, comma, or semicolon
          const parts = line.split(/\t|,|;/);
          if (parts.length >= 3) {
            const nationalId = parts[0].trim();
            const statusStr = [...parts]
              .reverse()
              .find(part => part.trim() !== '')
              ?.trim()
              .toLowerCase() || '';
            
            // Skip header row if it exists
            if (nationalId.toLowerCase().includes('id') || nationalId.toLowerCase().includes('cédula') || nationalId.toLowerCase().includes('cedula')) {
              continue;
            }

            const normalizedStatus = normalizeCompletionOutcome(statusStr);
            if (normalizedStatus) {
              updates.push({ nationalId, status: normalizedStatus });
            }
          }
        }

        if (updates.length > 0) {
          const token = localStorage.getItem('adminToken');
          const response = await fetch('/api/checks/bulk-update-status', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ updates })
          });

          if (response.ok) {
            const data = await response.json();
            const updatedCount = data?.data?.updated ?? 0;
            const errorCount = data?.data?.errors?.length ?? 0;
            const message = errorCount > 0
              ? `Updated ${updatedCount} background checks. ${errorCount} row(s) need review.`
              : `Updated ${updatedCount} background checks successfully.`;
            setAlert({ type: 'success', message });
            loadChecks();
          } else {
            const errorData = await response.json();
            setAlert({ type: 'error', message: errorData.message || 'Failed to update statuses' });
          }
        } else {
          setAlert({ type: 'info', message: 'No valid statuses found to update in the CSV.' });
        }
      } catch (err) {
        setAlert({ type: 'error', message: 'Error processing CSV file.' });
      } finally {
        setChecksLoading(false);
      }
    };
    reader.readAsText(file);
    // reset file input
    event.target.value = '';
  };

  const handleCreateCompany = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/register-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: companyForm.adminFirstName,
          lastName: companyForm.adminLastName,
          email: companyForm.adminEmail,
          password: companyForm.adminPassword,
          companyName: companyForm.name,
          companyEmail: companyForm.email,
          phone: companyForm.phone,
          industry: companyForm.industry,
          size: companyForm.size,
          street: companyForm.street,
          city: companyForm.city,
          state: companyForm.state,
          zipCode: companyForm.zipCode,
          website: companyForm.website
        }),
      });

      if (response.ok) {
        setAlert({ type: 'success', message: 'Company created successfully!' });
        setCompanyDialogOpen(false);
        setCompanyForm({
          name: '',
          industry: '',
          address: '',
          phone: '',
          email: '',
          website: '',
          size: '1-10',
          adminFirstName: '',
          adminLastName: '',
          adminEmail: '',
          adminPassword: '',
          street: '',
          city: '',
          state: '',
          zipCode: ''
        });
        setEditingCompany(null);
        loadCompanies();
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.message || 'Failed to create company' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Network error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name,
      industry: company.industry,
      address: company.address,
      phone: company.phone,
      email: company.email,
      website: company.website || '',
      size: '1-10',
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
      adminPassword: '',
      street: '',
      city: '',
      state: '',
      zipCode: ''
    });
    setCompanyDialogOpen(true);
  };

  const handleUpdateCompany = async () => {
    if (!editingCompany) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/auth/companies/${editingCompany.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyName: companyForm.name,
          industry: companyForm.industry,
          address: companyForm.address,
          phone: companyForm.phone,
          email: companyForm.email,
          website: companyForm.website
        }),
      });

      if (response.ok) {
        setAlert({ type: 'success', message: 'Company updated successfully!' });
        setCompanyDialogOpen(false);
        setCompanyForm({
        name: '',
        industry: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        size: '1-10',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminPassword: '',
        street: '',
        city: '',
        state: '',
        zipCode: ''
      });
        setEditingCompany(null);
        loadCompanies();
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.message || 'Failed to update company' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Network error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewCompany = (company: Company) => {
    setViewingCompany(company);
    setViewDialogOpen(true);
  };

  const handleCreateUser = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/request-join-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: userForm.firstName,
          lastName: userForm.lastName,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role,
          companyId: userForm.companyId,
          message: `User created by Validado admin`
        }),
      });

      if (response.ok) {
        setAlert({ type: 'success', message: 'User created successfully! Approval may be required.' });
        setUserDialogOpen(false);
        setUserForm({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          role: 'employee',
          companyId: '',
          department: '',
          position: ''
        });
        loadUsers();
        loadPendingApprovals();
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.message || 'Failed to create user' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Network error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(companySearch.toLowerCase()) ||
    company.email.toLowerCase().includes(companySearch.toLowerCase())
  );

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.firstName.toLowerCase().includes(userSearch.toLowerCase()) ||
                         user.lastName.toLowerCase().includes(userSearch.toLowerCase()) ||
                         user.email.toLowerCase().includes(userSearch.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <ValidadoLogo />
            <Typography variant="h4" component="h1" gutterBottom>
              Validado Admin Portal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Secure access for Validado administrators
            </Typography>
          </Box>
          
          <Box component="form" onSubmit={(e: React.FormEvent<HTMLFormElement>) => { e.preventDefault(); handleLogin(); }}>
            <TextField
              fullWidth
              label="Admin Email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              margin="normal"
              required
            />
            {loginError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {loginError}
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <ValidadoLogo />
          <Typography variant="h4" component="h1" sx={{ ml: 2 }}>
            Validado Admin Portal
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => setIsAuthenticated(false)}
        >
          Logout
        </Button>
      </Box>

      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={() => setAlert(null)}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      <Paper elevation={2}>
        <Tabs 
          value={currentTab} 
          onChange={(_: React.SyntheticEvent, newValue: number) => setCurrentTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<Business />} 
            label="Companies" 
            iconPosition="start"
          />
          <Tab 
            icon={<People />} 
            label="Users" 
            iconPosition="start"
          />
          <Tab 
            icon={
              <Badge badgeContent={pendingApprovals.length} color="error">
                <PendingActions />
              </Badge>
            } 
            label="Pending Approvals" 
            iconPosition="start"
          />
          <Tab 
            icon={
              <Badge badgeContent={checksStats.pending} color="warning">
                <Assignment />
              </Badge>
            } 
            label="Background Checks" 
            iconPosition="start"
          />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Company Management</Typography>
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                placeholder="Search companies..."
                value={companySearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanySearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCompanyDialogOpen(true)}
              >
                Create Company
              </Button>
              <IconButton onClick={loadCompanies}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Employees</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>{company.name}</TableCell>
                    <TableCell>{company.industry}</TableCell>
                    <TableCell>{company.email}</TableCell>
                    <TableCell>{company.phone}</TableCell>
                    <TableCell>{getActualEmployeeCount(company.name)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={company.status} 
                        color={company.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Company Details">
                        <IconButton size="small" onClick={() => handleViewCompany(company)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Company">
                        <IconButton size="small" onClick={() => handleEditCompany(company)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">User Management</Typography>
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserSearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value as string)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setUserDialogOpen(true)}
              >
                Create User
              </Button>
              <IconButton onClick={loadUsers}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.companyName}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role} 
                        color={user.role === 'admin' ? 'primary' : user.role === 'manager' ? 'secondary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.status} 
                        color={user.status === 'approved' ? 'success' : user.status === 'pending' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.lastLogin || 'Never'}</TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEditUser(user)}>
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Pending Approvals</Typography>
            <IconButton onClick={loadPendingApprovals}>
              <Refresh />
            </IconButton>
          </Box>

          {pendingApprovals.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  No pending approvals at this time.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <List>
              {pendingApprovals.map((approval) => (
                <ListItem key={approval.id} divider>
                  <ListItemText
                    primary={`${approval.name} (${approval.email})`}
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          Type: {approval.type} | Company: {approval.companyName} | Role: {approval.role}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Requested: {new Date(approval.requestDate).toLocaleDateString()}
                        </Typography>
                        {approval.message && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Message: {approval.message}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      color="success" 
                      sx={{ mr: 1 }}
                      onClick={() => handleApproveUser(approval.id, 'approve')}
                      disabled={loading}
                    >
                      <Check />
                    </IconButton>
                    <IconButton 
                      color="error"
                      onClick={() => handleApproveUser(approval.id, 'reject')}
                      disabled={loading}
                    >
                      <Close />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Background Checks Management</Typography>
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                placeholder="Search checks..."
                value={checksSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChecksSearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={checksStatusFilter}
                  label="Status"
                  onChange={(e: SelectChangeEvent) => setChecksStatusFilter(e.target.value as string)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                component="label"
                color="primary"
                startIcon={<Add />}
              >
                Upload Status CSV
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleFileUpload}
                />
              </Button>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Download Type</InputLabel>
                <Select
                  value={downloadMode}
                  label="Download Type"
                  onChange={(e: SelectChangeEvent) => setDownloadMode(e.target.value as DownloadMode)}
                >
                  <MenuItem value="status_sample">Sample Status Update CSV</MenuItem>
                  <MenuItem value="detailed_checks">Detailed Checks CSV</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExportCSV}
                color="success"
              >
                Download CSV
              </Button>
              <IconButton onClick={loadChecks}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>
          <Box display="flex" gap={2} mb={3} alignItems="center">
            <Typography variant="body2" color="text.secondary">Filters:</Typography>
            <TextField
              size="small"
              label="Request Date Start"
              type="date"
              value={requestDateStart}
              onChange={(e) => setRequestDateStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Request Date End"
              type="date"
              value={requestDateEnd}
              onChange={(e) => setRequestDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Last Updated Start"
              type="date"
              value={lastUpdatedStart}
              onChange={(e) => setLastUpdatedStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Last Updated End"
              type="date"
              value={lastUpdatedEnd}
              onChange={(e) => setLastUpdatedEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {(requestDateStart || requestDateEnd || lastUpdatedStart || lastUpdatedEnd) && (
              <Button size="small" onClick={() => {
                setRequestDateStart('');
                setRequestDateEnd('');
                setLastUpdatedStart('');
                setLastUpdatedEnd('');
              }}>Clear Dates</Button>
            )}
          </Box>

          {/* Bulk Actions */}
          {selectedChecks.length > 0 && (
            <Box display="flex" gap={2} mb={2} p={2} bgcolor="action.hover" borderRadius={1}>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {selectedChecks.length} selected
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleBulkStatusChange('in_progress')}
                disabled={loading}
              >
                Mark In Progress
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={() => handleBulkStatusChange('completed_clear')}
                disabled={loading}
              >
                Complete + Clear
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleBulkStatusChange('completed_not_clear')}
                disabled={loading}
              >
                Complete + Not Clear
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => setSelectedChecks([])}
              >
                Clear Selection
              </Button>
            </Box>
          )}
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Checks
                  </Typography>
                  <Typography variant="h4">
                    {checksStats.total}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Pending
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {checksStats.pending}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    In Progress
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {checksStats.inProgress}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {checksStats.completed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Failed
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {checksStats.failed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Bulk Operations Controls */}
          {selectedChecks.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedChecks.length} check{selectedChecks.length !== 1 ? 's' : ''} selected
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Bulk Status Change</InputLabel>
                  <Select
                    label="Bulk Status Change"
                    onChange={(e: SelectChangeEvent) => handleBulkStatusChange(e.target.value as string)}
                    value=""
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Select action</MenuItem>
                    <MenuItem value="pending">Set to Pending</MenuItem>
                    <MenuItem value="in_progress">Set to In Progress</MenuItem>
                    <MenuItem value="completed_clear">Set to Completed + Clear</MenuItem>
                    <MenuItem value="completed_not_clear">Set to Completed + Not Clear</MenuItem>
                    <MenuItem value="completed">Set to Completed Only</MenuItem>
                    <MenuItem value="failed">Set to Failed</MenuItem>
                    <MenuItem value="cancelled">Set to Cancelled</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleExportChecks}
                  size="small"
                >
                  Download Selected
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleBulkDelete}
                  size="small"
                >
                  Delete Selected
                </Button>
              </Box>
            </Paper>
          )}

          {/* Export All Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportChecks}
              size="small"
            >
              Download Filtered ({filteredChecks.length})
            </Button>
          </Box>

          {checksLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedChecks.length > 0 && selectedChecks.length < filteredChecks.length}
                        checked={filteredChecks.length > 0 && selectedChecks.length === filteredChecks.length}
                        onChange={() => handleSelectAllChecks()}
                      />
                    </TableCell>
                    <TableCell>National ID</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status / Result</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Request Date</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredChecks.map((check) => {
                    let parsedNotes: any = null;
                    try {
                      if (check.notes) parsedNotes = JSON.parse(check.notes);
                    } catch (e) {}
                    
                    return (
                    <TableRow key={check.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedChecks.includes(check.id)}
                          onChange={() => handleSelectCheck(check.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            National ID: {check.nationalId || check.client?.nationalId || 'N/A'}
                          </Typography>
                          {(check.client?.firstName && check.client?.lastName) ? (
                            <Typography variant="body2" color="text.secondary">
                              {check.client.firstName} {check.client.lastName}
                            </Typography>
                          ) : (parsedNotes?.firstName || parsedNotes?.lastName) ? (
                            <Typography variant="body2" color="text.secondary">
                              {parsedNotes.firstName || ''} {parsedNotes.lastName || ''}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>{check.company.name}</TableCell>
                      <TableCell>{check.type}</TableCell>
                      <TableCell>
                        <Chip 
                          label={check.status.replace('_', ' ')} 
                          color={getStatusColor(check.status) as any}
                          size="small"
                          sx={{ mb: check.overallResult && check.overallResult !== 'pending' ? 0.5 : 0 }}
                        />
                        {check.overallResult && check.overallResult !== 'pending' && (
                          <Chip 
                            label={check.overallResult.replace('_', ' ')} 
                            color={check.overallResult === 'clear' ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={check.priority} 
                          color={getPriorityColor(check.priority) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(check.requestDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {check.completionDate ? new Date(check.completionDate).toLocaleDateString() : (check as any).updatedAt ? new Date((check as any).updatedAt).toLocaleDateString() : (check as any).updated_at ? new Date((check as any).updated_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value=""
                              onChange={(e: SelectChangeEvent) => handleStatusChange(check.id, e.target.value as string)}
                              displayEmpty
                              size="small"
                            >
                              <MenuItem value="" disabled>Update...</MenuItem>
                              <MenuItem value="pending">Set Pending</MenuItem>
                              <MenuItem value="in_progress">Set In Progress</MenuItem>
                              <MenuItem value="completed_clear">Set Completed + Clear</MenuItem>
                              <MenuItem value="completed_not_clear">Set Completed + Not Clear</MenuItem>
                              <MenuItem value="completed">Set Completed Only</MenuItem>
                              <MenuItem value="failed">Set Failed</MenuItem>
                              <MenuItem value="cancelled">Set Cancelled</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewCheck(check)}
                          >
                            <Visibility />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {filteredChecks.length === 0 && !checksLoading && (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  No background checks found.
                </Typography>
              </CardContent>
            </Card>
          )}
        </TabPanel>
      </Paper>

      {/* Company Creation Dialog */}
      <Dialog 
        open={companyDialogOpen} 
        onClose={() => setCompanyDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingCompany ? 'Edit Company' : 'Create New Company'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>Company Information</Typography>
              <TextField
                fullWidth
                label="Company Name"
                value={companyForm.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, name: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Industry"
                value={companyForm.industry}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, industry: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Address"
                value={companyForm.address}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, address: e.target.value })}
                margin="normal"
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Phone"
                value={companyForm.phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Company Email"
                type="email"
                value={companyForm.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, email: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Website"
                value={companyForm.website}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, website: e.target.value })}
                margin="normal"
              />
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Company Size</InputLabel>
                <Select
                  value={companyForm.size}
                  label="Company Size"
                  onChange={(e: SelectChangeEvent) => setCompanyForm({ ...companyForm, size: e.target.value as string })}
                >
                  <MenuItem value="1-10">1-10 employees</MenuItem>
                  <MenuItem value="11-50">11-50 employees</MenuItem>
                  <MenuItem value="51-200">51-200 employees</MenuItem>
                  <MenuItem value="201-500">201-500 employees</MenuItem>
                  <MenuItem value="501-1000">501-1000 employees</MenuItem>
                  <MenuItem value="1000+">1000+ employees</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Street Address"
                value={companyForm.street}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, street: e.target.value })}
                margin="normal"
                required
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="City"
                  value={companyForm.city}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, city: e.target.value })}
                  margin="normal"
                  required
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="State"
                  value={companyForm.state}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, state: e.target.value })}
                  margin="normal"
                  required
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Zip Code"
                  value={companyForm.zipCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, zipCode: e.target.value })}
                  margin="normal"
                  required
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
            {!editingCompany && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>Admin User</Typography>
                <TextField
                  fullWidth
                  label="First Name"
                  value={companyForm.adminFirstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, adminFirstName: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={companyForm.adminLastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, adminLastName: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Admin Email"
                  type="email"
                  value={companyForm.adminEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, adminEmail: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={companyForm.adminPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyForm({ ...companyForm, adminPassword: e.target.value })}
                  margin="normal"
                  required
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCompanyDialogOpen(false);
            setEditingCompany(null);
            setCompanyForm({
              name: '',
              industry: '',
              address: '',
              phone: '',
              email: '',
              website: '',
              size: '1-10',
              adminFirstName: '',
              adminLastName: '',
              adminEmail: '',
              adminPassword: '',
              street: '',
              city: '',
              state: '',
              zipCode: ''
            });
          }}>Cancel</Button>
          <Button 
            onClick={editingCompany ? handleUpdateCompany : handleCreateCompany} 
            variant="contained"
            disabled={loading || !companyForm.name || (!editingCompany && (!companyForm.adminEmail || !companyForm.adminPassword))}
          >
            {loading ? (editingCompany ? 'Updating...' : 'Creating...') : (editingCompany ? 'Update Company' : 'Create Company')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company View Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Company Details</DialogTitle>
        <DialogContent>
          {viewingCompany && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Company Name</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCompany.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Industry</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCompany.industry}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCompany.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCompany.phone}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Website</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCompany.website || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={viewingCompany.status} 
                    color={viewingCompany.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCompany.address}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Employee Count</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCompany.employeeCount}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Created Date</Typography>
                  <Typography variant="body1" gutterBottom>{new Date(viewingCompany.createdAt).toLocaleDateString()}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {viewingCompany && (
            <Button 
              onClick={() => {
                setViewDialogOpen(false);
                handleEditCompany(viewingCompany);
              }}
              variant="contained"
              startIcon={<Edit />}
            >
              Edit Company
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* User Creation/Edit Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="First Name"
            value={userForm.firstName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserForm({ ...userForm, firstName: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Last Name"
            value={userForm.lastName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserForm({ ...userForm, lastName: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={userForm.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserForm({ ...userForm, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={editingUser ? "Password (leave blank to keep current)" : "Password"}
            type="password"
            value={userForm.password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserForm({ ...userForm, password: e.target.value })}
            margin="normal"
            required={!editingUser}
          />
          <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                onChange={(e: SelectChangeEvent) => setUserForm({ ...userForm, role: e.target.value as 'admin' | 'manager' | 'employee' })}
                label="Role"
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="employee">Employee</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Department"
              value={userForm.department}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserForm({ ...userForm, department: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Position"
              value={userForm.position}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserForm({ ...userForm, position: e.target.value })}
              margin="normal"
            />
          <FormControl fullWidth margin="normal">
            <InputLabel>Company</InputLabel>
            <Select
              value={userForm.companyId}
              onChange={(e: SelectChangeEvent) => setUserForm({ ...userForm, companyId: e.target.value })}
              label="Company"
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editingUser ? handleUpdateUser : handleCreateUser} 
            variant="contained"
            disabled={loading || !userForm.firstName || !userForm.lastName || !userForm.email || (!editingUser && !userForm.password) || !userForm.companyId}
          >
            {loading ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Create User')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check View Dialog */}
      <Dialog 
        open={checkViewDialogOpen} 
        onClose={() => setCheckViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Background Check Details</DialogTitle>
        <DialogContent>
          {selectedCheck && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">National ID</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedCheck.nationalId || selectedCheck.client?.nationalId || 'N/A'}
                  </Typography>
                </Grid>
                {selectedCheck.client?.firstName && selectedCheck.client?.lastName && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Client Name</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedCheck.client.firstName} {selectedCheck.client.lastName}
                    </Typography>
                  </Grid>
                )}
                {selectedCheck.client?.email && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Client Email</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedCheck.client.email}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                  <Typography variant="body1" gutterBottom>{selectedCheck.company.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Check Type</Typography>
                  <Typography variant="body1" gutterBottom>{selectedCheck.type}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedCheck.status.replace('_', ' ')} 
                    color={getStatusColor(selectedCheck.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                  <Chip 
                    label={selectedCheck.priority} 
                    color={getPriorityColor(selectedCheck.priority) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Request Date</Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(selectedCheck.requestDate).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Requested By</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedCheck.requestedBy.firstName} {selectedCheck.requestedBy.lastName}
                  </Typography>
                </Grid>
                {selectedCheck.estimatedCompletionDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Estimated Completion</Typography>
                    <Typography variant="body1" gutterBottom>
                      {new Date(selectedCheck.estimatedCompletionDate).toLocaleDateString()}
                    </Typography>
                  </Grid>
                )}
                {selectedCheck.completionDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Completion Date</Typography>
                    <Typography variant="body1" gutterBottom>
                      {new Date(selectedCheck.completionDate).toLocaleDateString()}
                    </Typography>
                  </Grid>
                )}
                {selectedCheck.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1" gutterBottom>{selectedCheck.notes}</Typography>
                  </Grid>
                )}
                {selectedCheck.results && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Results</Typography>
                    <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedCheck.results, null, 2)}
                      </pre>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ValidadoAdminPage;
